import { useMutation } from "@apollo/client/react";
import LoadingButton from "@repo/components/src/shared/loadingButton";
import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@repo/components/src/ui/avatar";
import { Badge, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { downloadFile } from "@repo/components/src/utils/downloadFile";
import { formatMoneyFromFragment } from "@repo/components/src/utils/money";
import {
  formatNumber,
  formatPercentage,
  formatSeparators,
} from "@repo/components/src/utils/numbers";
import { backendUrl } from "@repo/graphql/cache/url";
import { CampaignAnalyticFragmentFragment } from "@repo/graphql/documents/campaign/fragment.generated";
import { ExportCampaignAnalyticsDocument } from "@repo/graphql/documents/campaign/mutation.generated";
import { ExternalPathString, Link } from "expo-router";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  Download,
  ExternalLink,
} from "lucide-react-native";
import React, { useMemo, useState } from "react";
import { platformLabel } from "../shared/utils";

type WorkOrder = CampaignAnalyticFragmentFragment["workOrders"][number];
type Detail = WorkOrder["details"][number];

type Props = {
  workOrders: WorkOrder[];
  campaignId: string;
};

const EMV_RATE = 5;

// Screenshot-reported Stories never carry a permalink (they expire, and the
// story link is optional), so gating purely on `postUrl` hid every one of them
// from the table even though their metrics are verified and stored.
const hasConnectedPost = (detail: Detail) =>
  detail.isScreenshotReported ||
  (!!detail.postUrl && detail.postUrl.length > 0);
const connectedDetails = (wo: WorkOrder) => wo.details.filter(hasConnectedPost);
const woEmv = (wo: WorkOrder) => wo.views * EMV_RATE;
const detailEmv = (d: Detail) => d.views * EMV_RATE;
const woReplyCount = (wo: WorkOrder) =>
  wo.details.reduce((sum, d) => sum + d.replyCount, 0);
const woLinkClicks = (wo: WorkOrder) =>
  wo.details.reduce((sum, d) => sum + d.linkClicks, 0);

const moneyAmount = (
  money:
    | CampaignAnalyticFragmentFragment["workOrders"][number]["investment"]
    | null
    | undefined,
): number => {
  return Number(money?.amount ?? 0);
};

const NUM_COL_CLASS = "w-[96px] shrink-0 px-3 py-2";
const CREATOR_COL_CLASS = "w-[240px] shrink-0 px-3 py-2";
const ACTION_COL_CLASS = "w-[56px] shrink-0 px-3 py-2 items-end";

type SortKey =
  | "name"
  | "cost"
  | "views"
  | "reach"
  | "interactions"
  | "er"
  | "cpm"
  | "cpe"
  | "emv"
  | "likes"
  | "comments"
  | "shares"
  | "saves"
  | "replyCount"
  | "linkClicks";

type SortDir = "asc" | "desc";

const numericSortValue = (wo: WorkOrder, key: SortKey): number => {
  switch (key) {
    case "cost":
      return moneyAmount(wo.investment);
    case "views":
      return wo.views;
    case "reach":
      return wo.reach;
    case "interactions":
      return wo.interactions;
    case "er":
      return wo.engagementRate ?? 0;
    case "cpm":
      return moneyAmount(wo.cpm);
    case "cpe":
      return moneyAmount(wo.cpe);
    case "emv":
      return woEmv(wo);
    case "likes":
      return wo.likes;
    case "comments":
      return wo.comments;
    case "shares":
      return wo.shares;
    case "saves":
      return wo.saves;
    case "replyCount":
      return woReplyCount(wo);
    case "linkClicks":
      return woLinkClicks(wo);
    case "name":
      return 0;
  }
};

const DEFAULT_DIR: Record<SortKey, SortDir> = {
  name: "asc",
  cost: "desc",
  views: "desc",
  reach: "desc",
  interactions: "desc",
  er: "desc",
  cpm: "asc",
  cpe: "asc",
  emv: "desc",
  likes: "desc",
  comments: "desc",
  shares: "desc",
  saves: "desc",
  replyCount: "desc",
  linkClicks: "desc",
};

const CreatorsTable = React.memo(({ workOrders, campaignId }: Props) => {
  const displayedWorkOrders = useMemo(
    () => workOrders.filter((wo) => connectedDetails(wo).length > 0),
    [workOrders],
  );

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: "reach",
    dir: "desc",
  });

  const sortedWorkOrders = useMemo(() => {
    const copy = [...displayedWorkOrders];
    const dir = sort.dir === "asc" ? 1 : -1;
    if (sort.key === "name") {
      copy.sort((a, b) => a.fullName.localeCompare(b.fullName) * dir);
    } else {
      copy.sort(
        (a, b) =>
          (numericSortValue(a, sort.key) - numericSortValue(b, sort.key)) * dir,
      );
    }
    return copy;
  }, [displayedWorkOrders, sort]);

  const toggleExpand = (id: string) => {
    setExpanded((curr) => {
      const next = new Set(curr);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSort = (key: SortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : { key, dir: DEFAULT_DIR[key] },
    );
  };

  const [exportAnalytics, { loading: exporting }] = useMutation(
    ExportCampaignAnalyticsDocument,
    {
      onCompleted: (data) => {
        const file = data.campaign.exportAnalytics.file;
        downloadFile(file.url, "detalle-creadores.xlsx", backendUrl);
      },
    },
  );

  const handleExport = () => {
    exportAnalytics({ variables: { campaignId } });
  };

  if (!displayedWorkOrders.length) {
    return (
      <Text size="sm" className="text-typography-500">
        Aún no hay creadores con publicaciones conectadas. Pídeles que carguen
        el link de su publicación para verlas aquí.
      </Text>
    );
  }

  return (
    <VStack space="sm">
      <HStack className="justify-end">
        <LoadingButton
          size="xs"
          variant="outline"
          action="muted"
          rounded="lg"
          onPress={handleExport}
          loading={exporting}
          startIcon={Download}
          loadingText="Cargando ..."
        >
          Exportar Excel
        </LoadingButton>
      </HStack>
      <Box className="border border-outline-100 rounded-2xl overflow-x-auto">
        <Box style={{ minWidth: 1640 } as object}>
          <HStack className="bg-background-50 border-b border-outline-100">
            <HeaderCell
              className={CREATOR_COL_CLASS}
              sortKey="name"
              sort={sort}
              onSort={handleSort}
            >
              Creador
            </HeaderCell>
            <HeaderCell
              className={NUM_COL_CLASS}
              align="right"
              sortKey="cost"
              sort={sort}
              onSort={handleSort}
            >
              Costo
            </HeaderCell>
            <HeaderCell
              className={NUM_COL_CLASS}
              align="right"
              sortKey="views"
              sort={sort}
              onSort={handleSort}
            >
              Reprod.
            </HeaderCell>
            <HeaderCell
              className={NUM_COL_CLASS}
              align="right"
              sortKey="reach"
              sort={sort}
              onSort={handleSort}
            >
              Alcance
            </HeaderCell>
            <HeaderCell
              className={NUM_COL_CLASS}
              align="right"
              sortKey="interactions"
              sort={sort}
              onSort={handleSort}
            >
              Interacc.
            </HeaderCell>
            <HeaderCell
              className={NUM_COL_CLASS}
              align="right"
              sortKey="er"
              sort={sort}
              onSort={handleSort}
            >
              ER
            </HeaderCell>
            <HeaderCell
              className={NUM_COL_CLASS}
              align="right"
              sortKey="emv"
              sort={sort}
              onSort={handleSort}
            >
              EMV
            </HeaderCell>
            <HeaderCell
              className={NUM_COL_CLASS}
              align="right"
              sortKey="cpm"
              sort={sort}
              onSort={handleSort}
            >
              CPM
            </HeaderCell>
            <HeaderCell
              className={NUM_COL_CLASS}
              align="right"
              sortKey="cpe"
              sort={sort}
              onSort={handleSort}
            >
              CPE
            </HeaderCell>
            <HeaderCell
              className={NUM_COL_CLASS}
              align="right"
              sortKey="likes"
              sort={sort}
              onSort={handleSort}
            >
              Likes
            </HeaderCell>
            <HeaderCell
              className={NUM_COL_CLASS}
              align="right"
              sortKey="comments"
              sort={sort}
              onSort={handleSort}
            >
              Comments
            </HeaderCell>
            <HeaderCell
              className={NUM_COL_CLASS}
              align="right"
              sortKey="shares"
              sort={sort}
              onSort={handleSort}
            >
              Shares
            </HeaderCell>
            <HeaderCell
              className={NUM_COL_CLASS}
              align="right"
              sortKey="saves"
              sort={sort}
              onSort={handleSort}
            >
              Saved
            </HeaderCell>
            <HeaderCell
              className={NUM_COL_CLASS}
              align="right"
              sortKey="replyCount"
              sort={sort}
              onSort={handleSort}
            >
              Replies
            </HeaderCell>
            <HeaderCell
              className={NUM_COL_CLASS}
              align="right"
              sortKey="linkClicks"
              sort={sort}
              onSort={handleSort}
            >
              Clicks
            </HeaderCell>
            <Box className={ACTION_COL_CLASS} />
          </HStack>

          {sortedWorkOrders.map((wo) => {
            const isExpanded = expanded.has(wo.id);
            const avatar = wo.avatar;
            const details = connectedDetails(wo);
            return (
              <Box key={wo.id}>
                <HStack className="items-center border-b border-outline-100 bg-white hover:bg-background-50">
                  <Box className={CREATOR_COL_CLASS}>
                    <HStack space="sm" className="items-center">
                      <Avatar size="sm">
                        <AvatarFallbackText>{wo.fullName}</AvatarFallbackText>
                        <AvatarImage source={{ uri: avatar?.url }} />
                      </Avatar>
                      <Text
                        size="sm"
                        className="text-typography-900"
                        numberOfLines={1}
                      >
                        {wo.fullName}
                      </Text>
                    </HStack>
                  </Box>
                  <NumCell>
                    <Badge action="success" size="sm">
                      <BadgeText>
                        {formatMoneyFromFragment(wo.investment)}
                      </BadgeText>
                    </Badge>
                  </NumCell>
                  <NumCell>{formatNumber(wo.views)}</NumCell>
                  <NumCell>{formatNumber(wo.reach)}</NumCell>
                  <NumCell>{formatNumber(wo.interactions)}</NumCell>
                  <NumCell>{formatPercentage(wo.engagementRate ?? 0)}</NumCell>
                  <NumCell>{formatSeparators(woEmv(wo))}</NumCell>
                  <NumCell>{formatMoneyFromFragment(wo.cpm)}</NumCell>
                  <NumCell>{formatMoneyFromFragment(wo.cpe)}</NumCell>
                  <NumCell>{formatNumber(wo.likes)}</NumCell>
                  <NumCell>{formatNumber(wo.comments)}</NumCell>
                  <NumCell>{formatNumber(wo.shares)}</NumCell>
                  <NumCell>{formatNumber(wo.saves)}</NumCell>
                  <NumCell>{formatNumber(woReplyCount(wo))}</NumCell>
                  <NumCell>{formatNumber(woLinkClicks(wo))}</NumCell>
                  <Box className={ACTION_COL_CLASS}>
                    <Pressable
                      onPress={() => toggleExpand(wo.id)}
                      className="p-1"
                    >
                      <Icon
                        as={ChevronDown}
                        size="sm"
                        className={
                          isExpanded
                            ? "text-brand-500 rotate-180"
                            : "text-typography-500"
                        }
                      />
                    </Pressable>
                  </Box>
                </HStack>
                {isExpanded
                  ? details.map((detail) => (
                      <ChildRow key={detail.id} detail={detail} />
                    ))
                  : null}
              </Box>
            );
          })}
        </Box>
      </Box>
    </VStack>
  );
});

CreatorsTable.displayName = "OrganicCreatorsTable";

const HeaderCell = ({
  className,
  align = "left",
  sortKey,
  sort,
  onSort,
  children,
}: {
  className: string;
  align?: "left" | "right";
  sortKey?: SortKey;
  sort?: { key: SortKey; dir: SortDir };
  onSort?: (key: SortKey) => void;
  children: React.ReactNode;
}) => {
  const isActive = !!sortKey && sort?.key === sortKey;
  const ArrowIcon = isActive
    ? sort?.dir === "asc"
      ? ArrowUp
      : ArrowDown
    : ArrowUpDown;
  const labelClass = `uppercase font-semibold ${align === "right" ? "text-right" : "text-left"} ${isActive ? "text-brand-500" : "text-typography-500"}`;
  const inner = (
    <HStack
      space="xs"
      className={`items-center ${align === "right" ? "justify-end" : "justify-start"}`}
    >
      <Text size="2xs" className={labelClass}>
        {children}
      </Text>
      {sortKey ? (
        <Icon
          as={ArrowIcon}
          size="2xs"
          className={isActive ? "text-brand-500" : "text-typography-300"}
        />
      ) : null}
    </HStack>
  );
  if (sortKey && onSort) {
    return (
      <Pressable className={className} onPress={() => onSort(sortKey)}>
        {inner}
      </Pressable>
    );
  }
  return <Box className={className}>{inner}</Box>;
};

const NumCell = ({ children }: { children: React.ReactNode }) => (
  <Box className={NUM_COL_CLASS + " items-end"}>
    {typeof children === "string" || typeof children === "number" ? (
      <Text size="sm" className="text-typography-700 text-right">
        {children}
      </Text>
    ) : (
      children
    )}
  </Box>
);

const ChildRow = ({ detail }: { detail: Detail }) => {
  return (
    <HStack className="items-center bg-background-50 border-b border-outline-100">
      <Box className={CREATOR_COL_CLASS + " pl-12"}>
        <Badge action="muted" variant="outline" size="sm">
          <BadgeText>
            {platformLabel(detail.platformCode)} · {detail.postName}
          </BadgeText>
        </Badge>
      </Box>
      <NumCell>—</NumCell>
      <NumCell>{formatNumber(detail.views)}</NumCell>
      <NumCell>{formatNumber(detail.reach)}</NumCell>
      <NumCell>{formatNumber(detail.interactions)}</NumCell>
      <NumCell>{formatPercentage(detail.engagementRate ?? 0)}</NumCell>
      <NumCell>{formatSeparators(detailEmv(detail))}</NumCell>
      <NumCell>{formatMoneyFromFragment(detail.cpm)}</NumCell>
      <NumCell>{formatMoneyFromFragment(detail.cpe)}</NumCell>
      <NumCell>{formatNumber(detail.likes)}</NumCell>
      <NumCell>{formatNumber(detail.comments)}</NumCell>
      <NumCell>{formatNumber(detail.shares)}</NumCell>
      <NumCell>{formatNumber(detail.saves)}</NumCell>
      <NumCell>{formatNumber(detail.replyCount)}</NumCell>
      <NumCell>{formatNumber(detail.linkClicks)}</NumCell>
      <Box className={ACTION_COL_CLASS}>
        {detail.postUrl ? (
          <Link href={detail.postUrl as ExternalPathString} target="_blank">
            <Icon as={ExternalLink} size="sm" className="text-typography-500" />
          </Link>
        ) : null}
      </Box>
    </HStack>
  );
};

export default CreatorsTable;
